// @ts-check
// Campo do código de 6 dígitos enviado por e-mail e o botão de reenvio com espera, usados no login e no cadastro.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';

export const EMAIL_CODE_LENGTH = 6;
// o backend recusa um segundo envio para o mesmo e-mail antes disso
export const RESEND_COOLDOWN_SECONDS = 60;

/** @param {string} value */
export const sanitizeEmailCode = (value) => value.replace(/\D/g, '').slice(0, EMAIL_CODE_LENGTH);

/**
 * Seis caixas, um dígito em cada; colar ou o preenchimento automático do celular distribui o código inteiro.
 * @param {{ value: string, onInput: (value: string) => void, onComplete?: (value: string) => void, error?: string | null,
 *   hint?: string | null, disabled?: boolean, autoFocus?: boolean, label?: string }} props
 */
export function EmailCodeField({ value, onInput, onComplete, error, hint, disabled = false, autoFocus = true, label }) {
  const groupReference = useRef(/** @type {HTMLDivElement | null} */ (null));
  const fieldLabel = label ?? t('auth.emailCode.label');

  /** @param {number} index */
  const focusBox = (index) => {
    const boxes = groupReference.current?.querySelectorAll('input');
    const box = boxes?.[Math.max(0, Math.min(EMAIL_CODE_LENGTH - 1, index))];
    if (box) {
      box.focus();
      box.select();
    }
  };

  useEffect(() => {
    if (autoFocus) focusBox(Math.min(value.length, EMAIL_CODE_LENGTH - 1));
  }, []);

  // depois de um código errado o campo é limpo; o foco volta para a primeira caixa quando ela é liberada
  useEffect(() => {
    if (error && !disabled) focusBox(0);
  }, [error, disabled]);

  /** @param {string} next */
  const commit = (next) => {
    const code = sanitizeEmailCode(next);
    const isNewlyComplete = code.length === EMAIL_CODE_LENGTH && code !== value;
    onInput(code);
    if (isNewlyComplete && onComplete) onComplete(code);
    return code;
  };

  /** @param {number} index @param {string} digits */
  const fillFrom = (index, digits) => {
    const start = Math.min(index, value.length);
    const code = commit(value.slice(0, start) + digits);
    focusBox(code.length);
  };

  const digits = Array.from({ length: EMAIL_CODE_LENGTH }, (_, index) => value.charAt(index));

  return html`<div class="auth-field">
    <span class="input-label" id="email-code-label">${fieldLabel}</span>
    <div class="auth-code-boxes" ref=${groupReference} role="group" aria-labelledby="email-code-label" data-error=${error ? 'true' : undefined}>
      ${digits.map(
        (digit, index) => html`<input
          key=${index}
          class="auth-code-box"
          name=${index === 0 ? 'emailCode' : undefined}
          type="text"
          inputmode="numeric"
          autocomplete=${index === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          aria-label=${t('auth.emailCode.digitLabel', { position: index + 1, total: EMAIL_CODE_LENGTH })}
          aria-invalid=${error ? 'true' : undefined}
          value=${digit}
          disabled=${disabled}
          onFocus=${(event) => event.currentTarget.select()}
          onInput=${(event) => {
            const input = event.currentTarget;
            const typed = input.value.replace(/\D/g, '');
            if (typed.length === 0) {
              input.value = digit;
              return;
            }
            // num campo já preenchido o navegador junta o dígito antigo com o novo; fica o que foi digitado
            const replacement = typed.length === 2 && digit && typed.includes(digit) ? typed.replace(digit, '') || digit : typed;
            if (replacement.length > 1) {
              fillFrom(index, replacement);
              return;
            }
            // o código não tem lacunas: digitar numa caixa adiante preenche a primeira vazia
            const position = Math.min(index, value.length);
            const code = commit(value.slice(0, position) + replacement + value.slice(position + 1));
            input.value = code.charAt(index);
            focusBox(position + 1);
          }}
          onPaste=${(event) => {
            event.preventDefault();
            const pasted = sanitizeEmailCode(event.clipboardData?.getData('text') ?? '');
            if (pasted) fillFrom(index, pasted);
          }}
          onKeyDown=${(event) => {
            if (event.key === 'Backspace') {
              event.preventDefault();
              if (digit) commit(value.slice(0, index) + value.slice(index + 1));
              else if (index > 0) {
                commit(value.slice(0, index - 1) + value.slice(index));
                focusBox(index - 1);
              }
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              focusBox(index - 1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              focusBox(index + 1);
            }
          }}
        />`,
      )}
    </div>
    ${error
      ? html`<span class="input-hint" data-danger="true" role="alert">${error}</span>`
      : hint
        ? html`<span class="input-hint">${hint}</span>`
        : null}
  </div>`;
}

/**
 * Segundos que faltam para liberar o reenvio, atualizado a cada segundo.
 * @param {number | null} availableAt instante (ms) em que o reenvio fica liberado
 */
export function useCooldownSeconds(availableAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!availableAt) return undefined;
    setNow(Date.now());
    const interval = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= availableAt) window.clearInterval(interval);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [availableAt]);
  return availableAt ? Math.max(0, Math.ceil((availableAt - now) / 1000)) : 0;
}

/**
 * @param {{ secondsLeft: number, isSending: boolean, onResend: () => void }} props
 */
export function ResendCodeButton({ secondsLeft, isSending, onResend }) {
  const isWaiting = secondsLeft > 0;
  return html`<button type="button" class="auth-link-button" disabled=${isWaiting || isSending} onClick=${onResend}>
    ${isSending ? t('auth.emailCode.resending') : isWaiting ? t('auth.emailCode.resendIn', { seconds: secondsLeft }) : t('auth.emailCode.resend')}
  </button>`;
}
